import { useEffect, useRef, useState } from 'react';

// Generic hook for working with controlled Range inputs + committing after delay
export function useDebouncedRange(
  storeRange: { min: number | null; max: number | null },
  onCommit: (min: number | null, max: number | null) => void,
  delay = 600
) {
  const [ minLocal, setMinLocal ] = useState<number | undefined>(storeRange.min ?? undefined);
  const [ maxLocal, setMaxLocal ] = useState<number | undefined>(storeRange.max ?? undefined);

  const isLocalUpdate = useRef(false);

  // Commit local → store (debounced)
  useEffect(() => {
    isLocalUpdate.current = true;

    const timer = setTimeout(() => {
      onCommit(minLocal ?? null, maxLocal ?? null);
      // allow store-to-local sync again
      setTimeout(() => (isLocalUpdate.current = false), 0);
    }, delay);

    return () => clearTimeout(timer);
  }, [ minLocal, maxLocal ]);

  // Store → local sync (reset, preset changes)
  useEffect(() => {
    if (!isLocalUpdate.current) {
      setMinLocal(storeRange.min ?? undefined);
      setMaxLocal(storeRange.max ?? undefined);
    }
  }, [ storeRange.min, storeRange.max ]);

  return {
    minLocal,
    maxLocal,
    setMinLocal,
    setMaxLocal,
  };
}
